import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTenant } from '../lib/tenant-context';
import { tenantFetch } from '../lib/api-client';

interface Campaign {
  id: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'paused' | 'completed';
  target_brands: string[];
  target_categories: string[];
  target_conditions: string[];
  target_min_price_cents: number | null;
  target_max_price_cents: number | null;
  offer_type: 'percentage' | 'fixed';
  offer_percentage: number | null;
  offer_fixed_cents: number | null;
  bonus_percentage: number;
  budget_cents: number | null;
  spent_cents: number;
  reserved_cents: number;
  offers_sent: number;
  max_offers: number | null;
  starts_at: number | null;
  ends_at: number | null;
  created_at: number;
  total_offers: number;
  accepted_offers: number;
}

// Icons
const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="5 3 19 12 5 21 5 3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PauseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="6" y="4" width="4" height="16" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="14" y="4" width="4" height="16" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TargetIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="6"/>
    <circle cx="12" cy="12" r="2"/>
  </svg>
);

const DollarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23" strokeLinecap="round"/>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="20" x2="18" y2="10" strokeLinecap="round"/>
    <line x1="12" y1="20" x2="12" y2="4" strokeLinecap="round"/>
    <line x1="6" y1="20" x2="6" y2="14" strokeLinecap="round"/>
  </svg>
);

export const Route = createFileRoute('/campaigns_/$id')({
  component: CampaignDetail,
});

function CampaignDetail() {
  const { id } = Route.useParams();
  const { currentTenant } = useTenant();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Campaign>>({});

  useEffect(() => {
    fetchCampaign();
  }, [id, currentTenant.id]);

  const fetchCampaign = async () => {
    setLoading(true);
    try {
      const response = await tenantFetch(`/api/v1/campaigns/${id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setCampaign(data.data);
          setLoading(false);
          return;
        }
      }
      // Fall back to demo data
      setCampaign(getDemoCampaign(id, currentTenant.id));
    } catch {
      setCampaign(getDemoCampaign(id, currentTenant.id));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: Campaign['status']) => {
    if (!campaign) return;
    try {
      const response = await tenantFetch(`/api/v1/campaigns/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        setCampaign({ ...campaign, status: newStatus });
      }
    } catch {
      // For demo, just update locally
      setCampaign({ ...campaign, status: newStatus });
    }
  };

  const handleSave = async () => {
    if (!campaign) return;
    try {
      const response = await tenantFetch(`/api/v1/campaigns/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCampaign({ ...campaign, ...editForm });
        }
      }
    } catch {
      // For demo, just update locally
      setCampaign({ ...campaign, ...editForm });
    }
    setIsEditing(false);
    setEditForm({});
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  };

  const formatPercent = (value: number) => {
    return `${Math.round(value * 100)}%`;
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Not set';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: '#171717' }} />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="text-center py-12">
          <p className="text-lg" style={{ color: '#A9A9A9' }}>Campaign not found</p>
          <Link to="/campaigns" className="text-sm underline mt-4 inline-block" style={{ color: '#171717' }}>
            Back to Campaigns
          </Link>
        </div>
      </div>
    );
  }

  const acceptanceRate = campaign.total_offers > 0 
    ? Math.round((campaign.accepted_offers / campaign.total_offers) * 100) 
    : 0;

  const budgetUsed = campaign.budget_cents 
    ? Math.round(((campaign.spent_cents + campaign.reserved_cents) / campaign.budget_cents) * 100)
    : 0;

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="mb-8">
        <Link 
          to="/campaigns" 
          className="inline-flex items-center gap-2 text-sm mb-4 hover:underline"
          style={{ color: '#A9A9A9' }}
        >
          <ArrowLeftIcon />
          Back to Campaigns
        </Link>
        
        <div className="flex items-start justify-between">
          <div>
            {isEditing ? (
              <input
                type="text"
                value={editForm.name ?? campaign.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="text-2xl font-light border-b-2 bg-transparent outline-none"
                style={{ color: '#171717', borderColor: '#171717' }}
              />
            ) : (
              <h1 className="text-2xl font-light" style={{ color: '#171717' }}>
                {campaign.name}
              </h1>
            )}
            {isEditing ? (
              <textarea
                value={editForm.description ?? campaign.description ?? ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="text-sm mt-2 w-full border rounded p-2"
                style={{ color: '#A9A9A9' }}
                rows={2}
              />
            ) : (
              <p className="text-sm mt-1" style={{ color: '#A9A9A9' }}>
                {campaign.description || 'No description'}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {isEditing ? (
              <>
                <button
                  onClick={() => { setIsEditing(false); setEditForm({}); }}
                  className="px-4 py-2 rounded-full text-sm font-medium border"
                  style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#171717' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: '#171717' }}
                >
                  Save Changes
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border hover:bg-gray-50"
                  style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#171717' }}
                >
                  <EditIcon />
                  Edit
                </button>
                {campaign.status === 'active' ? (
                  <button
                    onClick={() => handleStatusChange('paused')}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: '#E65100' }}
                  >
                    <PauseIcon />
                    Pause
                  </button>
                ) : campaign.status === 'paused' || campaign.status === 'draft' ? (
                  <button
                    onClick={() => handleStatusChange('active')}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: '#2E7D32' }}
                  >
                    <PlayIcon />
                    {campaign.status === 'draft' ? 'Launch' : 'Resume'}
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-4">
          <span
            className="px-3 py-1 rounded-full text-xs font-medium"
            style={{
              backgroundColor: campaign.status === 'active' ? '#E8F5E9' 
                : campaign.status === 'paused' ? '#FFF3E0' 
                : campaign.status === 'draft' ? '#E3F2FD'
                : '#F5F5F5',
              color: campaign.status === 'active' ? '#2E7D32' 
                : campaign.status === 'paused' ? '#E65100' 
                : campaign.status === 'draft' ? '#1565C0'
                : '#757575'
            }}
          >
            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <StatCard label="Offers Sent" value={campaign.offers_sent} suffix={campaign.max_offers ? `/ ${campaign.max_offers}` : ''} />
        <StatCard label="Accepted" value={campaign.accepted_offers} suffix={`(${acceptanceRate}%)`} />
        <StatCard label="Budget Used" value={formatCurrency(campaign.spent_cents)} suffix={campaign.budget_cents ? `/ ${formatCurrency(campaign.budget_cents)}` : ''} />
        <StatCard label="Reserved" value={formatCurrency(campaign.reserved_cents)} />
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Targeting */}
        <div className="p-6" style={{ backgroundColor: '#FAFAFA', border: '1px solid rgba(169,169,169,0.2)' }}>
          <div className="flex items-center gap-2 mb-4">
            <TargetIcon />
            <h2 className="text-lg font-medium" style={{ color: '#171717' }}>Targeting</h2>
          </div>
          
          <div className="space-y-3">
            <DetailRow label="Brands" value={campaign.target_brands?.join(', ') || 'Any'} />
            <DetailRow label="Categories" value={campaign.target_categories?.join(', ') || 'Any'} />
            <DetailRow label="Conditions" value={campaign.target_conditions?.join(', ') || 'Any'} />
            {(campaign.target_min_price_cents || campaign.target_max_price_cents) && (
              <DetailRow 
                label="Price Range" 
                value={`${campaign.target_min_price_cents ? formatCurrency(campaign.target_min_price_cents) : '$0'} - ${campaign.target_max_price_cents ? formatCurrency(campaign.target_max_price_cents) : 'Any'}`} 
              />
            )}
          </div>
        </div>

        {/* Offer Details */}
        <div className="p-6" style={{ backgroundColor: '#FAFAFA', border: '1px solid rgba(169,169,169,0.2)' }}>
          <div className="flex items-center gap-2 mb-4">
            <DollarIcon />
            <h2 className="text-lg font-medium" style={{ color: '#171717' }}>Offer Details</h2>
          </div>
          
          <div className="space-y-3">
            <DetailRow 
              label="Offer Type" 
              value={campaign.offer_type === 'percentage' 
                ? `${formatPercent(campaign.offer_percentage || 0)} of item value`
                : `${formatCurrency(campaign.offer_fixed_cents || 0)} fixed`} 
            />
            <DetailRow label="Bonus" value={formatPercent(campaign.bonus_percentage)} />
            <DetailRow label="Budget" value={campaign.budget_cents ? formatCurrency(campaign.budget_cents) : 'Unlimited'} />
            <DetailRow label="Max Offers" value={campaign.max_offers?.toString() || 'Unlimited'} />
          </div>
        </div>

        {/* Schedule */}
        <div className="p-6" style={{ backgroundColor: '#FAFAFA', border: '1px solid rgba(169,169,169,0.2)' }}>
          <div className="flex items-center gap-2 mb-4">
            <ChartIcon />
            <h2 className="text-lg font-medium" style={{ color: '#171717' }}>Schedule</h2>
          </div>
          
          <div className="space-y-3">
            <DetailRow label="Start Date" value={formatDate(campaign.starts_at)} />
            <DetailRow label="End Date" value={formatDate(campaign.ends_at)} />
            <DetailRow label="Created" value={formatDate(campaign.created_at)} />
          </div>
        </div>

        {/* Performance */}
        <div className="p-6" style={{ backgroundColor: '#FAFAFA', border: '1px solid rgba(169,169,169,0.2)' }}>
          <div className="flex items-center gap-2 mb-4">
            <ChartIcon />
            <h2 className="text-lg font-medium" style={{ color: '#171717' }}>Performance</h2>
          </div>
          
          <div className="space-y-3">
            <DetailRow label="Acceptance Rate" value={`${acceptanceRate}%`} />
            <DetailRow label="Budget Utilization" value={`${budgetUsed}%`} />
            <DetailRow label="Avg Offer Value" value={campaign.accepted_offers > 0 ? formatCurrency(campaign.spent_cents / campaign.accepted_offers) : 'N/A'} />
          </div>

          {/* Progress bar */}
          {campaign.budget_cents && (
            <div className="mt-4">
              <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(169,169,169,0.2)' }}>
                <div 
                  className="h-full rounded-full transition-all"
                  style={{ 
                    width: `${budgetUsed}%`,
                    backgroundColor: budgetUsed > 90 ? '#E65100' : budgetUsed > 70 ? '#FFB300' : '#2E7D32'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix }: { label: string; value: string | number; suffix?: string }) {
  return (
    <div className="p-4" style={{ backgroundColor: '#FAFAFA', border: '1px solid rgba(169,169,169,0.2)' }}>
      <p className="text-xs font-medium mb-1" style={{ color: '#A9A9A9' }}>{label}</p>
      <p className="text-2xl font-light" style={{ color: '#171717' }}>
        {value}
        {suffix && <span className="text-sm ml-1" style={{ color: '#A9A9A9' }}>{suffix}</span>}
      </p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-sm" style={{ color: '#A9A9A9' }}>{label}</span>
      <span className="text-sm font-medium" style={{ color: '#171717' }}>{value}</span>
    </div>
  );
}

function getDemoCampaign(id: string, _tenantId: string): Campaign | null {
  const now = Math.floor(Date.now() / 1000);
  
  const campaigns: Record<string, Campaign> = {
    'cmp_lulu_1': {
      id: 'cmp_lulu_1',
      name: 'Lululemon Winter Trade-In',
      description: 'Get credit for your gently used Lululemon items',
      status: 'active',
      target_brands: ['Lululemon'],
      target_categories: ['Clothing', 'Activewear'],
      target_conditions: ['A', 'B+', 'B'],
      target_min_price_cents: null,
      target_max_price_cents: null,
      offer_type: 'percentage',
      offer_percentage: 0.35,
      offer_fixed_cents: null,
      bonus_percentage: 0.15,
      budget_cents: 2500000,
      spent_cents: 875000,
      reserved_cents: 125000,
      offers_sent: 47,
      max_offers: 200,
      starts_at: now - 86400 * 14,
      ends_at: now + 86400 * 30,
      created_at: now - 86400 * 14,
      total_offers: 47,
      accepted_offers: 23
    },
    'cmp_arc_1': {
      id: 'cmp_arc_1',
      name: "Arc'teryx ReBird Trade-In",
      description: "Trade in your used Arc'teryx gear for store credit",
      status: 'active',
      target_brands: ["Arc'teryx"],
      target_categories: ['Outerwear', 'Clothing'],
      target_conditions: ['A', 'B+', 'B', 'B-'],
      target_min_price_cents: null,
      target_max_price_cents: null,
      offer_type: 'percentage',
      offer_percentage: 0.40,
      offer_fixed_cents: null,
      bonus_percentage: 0.10,
      budget_cents: 5000000,
      spent_cents: 1875000,
      reserved_cents: 250000,
      offers_sent: 156,
      max_offers: 500,
      starts_at: now - 86400 * 30,
      ends_at: now + 86400 * 60,
      created_at: now - 86400 * 30,
      total_offers: 156,
      accepted_offers: 89
    },
    'cmp_lego_1': {
      id: 'cmp_lego_1',
      name: 'LEGO Brick Buyback Program',
      description: 'Trade in your used LEGO bricks for store credit',
      status: 'active',
      target_brands: ['LEGO'],
      target_categories: ['Building Sets', 'Loose Bricks'],
      target_conditions: ['A', 'B+', 'B'],
      target_min_price_cents: null,
      target_max_price_cents: null,
      offer_type: 'percentage',
      offer_percentage: 0.30,
      offer_fixed_cents: null,
      bonus_percentage: 0.15,
      budget_cents: 10000000,
      spent_cents: 3420000,
      reserved_cents: 580000,
      offers_sent: 456,
      max_offers: 2000,
      starts_at: now - 86400 * 45,
      ends_at: now + 86400 * 120,
      created_at: now - 86400 * 45,
      total_offers: 456,
      accepted_offers: 342
    },
  };

  return campaigns[id] || null;
}

